import React, { useMemo, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";

export interface AssigneeOption {
	email: string;
	name: string;
}

/**
 * Assignee selector: the chosen users show as removable chips; a search field finds more (no list of
 * everyone). Selection is fully explicit : nothing is ever pre-selected : which avoids the "some users
 * look assigned though I didn't pick them" confusion of a checkbox-of-all-users list. Emails are
 * compared case-insensitively.
 */
export const AssigneePicker: React.FC<{
	value: string[];
	onChange: (next: string[]) => void;
	options: AssigneeOption[];
	disabled?: boolean;
	placeholder?: string;
}> = ({ value, onChange, options, disabled, placeholder }) => {
	const [query, setQuery] = useState("");

	const selectedLc = useMemo(() => new Set(value.map((e) => e.toLowerCase())), [value]);
	const byEmail = useMemo(() => new Map(options.map((o) => [o.email.toLowerCase(), o])), [options]);

	const q = query.trim().toLowerCase();
	const matches = useMemo(() => {
		if (!q) return [];
		return options
			.filter((o) => !selectedLc.has(o.email.toLowerCase()))
			.filter((o) => o.email.toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q))
			.slice(0, 6);
	}, [q, options, selectedLc]);

	const add = (email: string) => {
		if (!selectedLc.has(email.toLowerCase())) onChange([...value, email]);
		setQuery("");
	};
	const remove = (email: string) => onChange(value.filter((e) => e.toLowerCase() !== email.toLowerCase()));

	return (
		<div>
			{value.length > 0 && (
				<div className="mb-2 flex flex-wrap gap-1.5">
					{value.map((email) => {
						const o = byEmail.get(email.toLowerCase());
						return (
							<span key={email} className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-800" title={email}>
								{o?.name || email}
								{!disabled && (
									<button type="button" onClick={() => remove(email)} className="text-sky-500 hover:text-sky-700" aria-label={`Retirer ${o?.name || email}`}>
										<FiX className="h-3 w-3" />
									</button>
								)}
							</span>
						);
					})}
				</div>
			)}

			{!disabled && (
				<div className="relative">
					<div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-slate-400">
						<FiSearch className="h-4 w-4 shrink-0 text-slate-400" />
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={placeholder ?? "Rechercher un utilisateur…"}
							className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
						/>
					</div>
					{matches.length > 0 && (
						<div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
							{matches.map((o) => (
								<button key={o.email} type="button" onClick={() => add(o.email)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
									<span className="truncate font-medium text-slate-700">{o.name}</span>
									<span className="truncate text-xs text-slate-400">{o.email}</span>
								</button>
							))}
						</div>
					)}
					{q.length > 0 && matches.length === 0 && (
						<p className="mt-1 px-1 text-xs text-slate-400">Aucun utilisateur trouvé.</p>
					)}
				</div>
			)}
		</div>
	);
};

export default AssigneePicker;
